import { SitemapItemShapeType } from '~/client/graph'

import ThemeMode from '../../../utils/ThemeModeManager'
import { IGroundFill } from '../../MapBoxViewer/labelGroundTone'
import MapViewItemBase from '../models/MapViewItemBase'

// A location's shape is ground under any label standing on it. Without this a label over a dark
// zone took the BASEMAP's answer, so its ink flipped with the basemap while the fill never moved.
//
// No new mechanism needed: the shape is already a polygon with a colour and an opacity in app
// data, which is what IGroundFill describes. The filter mirrors buildFillShapesGeoJson, because
// the question is "what did that layer draw" and a second opinion would drift.

export function groundFillsFromLocations(
  items: MapViewItemBase[],
): IGroundFill[] {
  const fills: IGroundFill[] = []
  items.forEach(item => {
    const shape = item.sitemapItem?.shapeCoordinates
    const properties = item.globeViewItemProperties?.shapeProperties
    if (!shape?.coordinates?.length || !properties?.isDisplayed) return
    // An open polyline is a line, and a line paints no ground worth speaking of.
    if (shape.type === SitemapItemShapeType.Polyline && !shape.isClosed) return

    const opacity = properties.fillOpacity ?? 0
    if (opacity <= 0) return
    // Through the same converter the layer uses: a shape's colour is stored as a themed CSS
    // value like `hsla(111, 60%, var(--tone60), 1)`, so reading the raw field gets a string
    // no compositor can parse — which is exactly how this shipped doing nothing at first.
    const colorHex = normaliseHex(ThemeMode.getHEXColor(properties.fillColor))
    if (!colorHex) return

    // One fill per part, so a label standing on any part reads that part's colour, and a label
    // inside a hole reads the ground under the hole.
    polygonsOf(shape.getShapePolygonFeature({})?.geometry).forEach(
      ([ring, ...holes]) => {
        if (!ring || ring.length < 3) return
        fills.push({
          ring: ring as Array<[number, number]>,
          holes: holes as Array<Array<[number, number]>>,
          colorHex,
          opacity,
        })
      },
    )
  })
  return fills
}

function polygonsOf(
  geometry: GeoJSON.Polygon | GeoJSON.MultiPolygon | undefined,
): GeoJSON.Position[][][] {
  if (!geometry) return []
  return geometry.type === 'MultiPolygon'
    ? geometry.coordinates
    : [geometry.coordinates]
}

// Theme colours arrive as #rgb, #rrggbb or #rrggbbaa; the compositor carries opacity
// separately and parses six digits, so anything else is dropped rather than mis-read.
function normaliseHex(value: string | undefined): string | null {
  if (!value || value[0] !== '#') return null
  const digits = value.slice(1)
  if (digits.length === 3) {
    return `#${digits[0]}${digits[0]}${digits[1]}${digits[1]}${digits[2]}${digits[2]}`
  }
  if (digits.length >= 6) return `#${digits.slice(0, 6)}`
  return null
}
